import axios from "axios";
import {
  clearAccessToken,
  getAccessToken,
  notifySessionExpired,
  setAccessToken,
} from "./authToken";

/**
 * Browser-side HTTP client.
 *
 * Mirrors the server's retry policy so a flaky network on the user's side
 * behaves the same way a flaky upstream does on ours.
 *
 * - 10s per attempt for ordinary calls. Not 3s or 5s: a cold EUIPO register
 *   query plus AI scoring regularly runs past both, and cancelling at 5s
 *   turned slow-but-fine requests into visible errors.
 * - The search call gets a longer ceiling (SEARCH_TIMEOUT_MS) because it is
 *   a compound operation: register lookup, fallback, then model analysis.
 * - Retries network errors, timeouts, 408, 429 and 5xx. Never retries a 400
 *   or a 401 — those fail identically the second time.
 * - Exponential backoff with jitter, capped, honouring `Retry-After`.
 */

export const HTTP_TIMEOUT_MS = 10000;
export const SEARCH_TIMEOUT_MS = 90000;
export const MAX_RETRIES = 3;

const RETRY_BASE_DELAY_MS = 500;
const RETRY_MAX_DELAY_MS = 4000;
const RETRYABLE_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function isRetryable(error) {
  if (axios.isCancel?.(error)) return false;
  if (error.response) return RETRYABLE_STATUS.has(error.response.status);
  return true; // network failure or timeout: the request never landed
}

function backoffDelay(attempt, error) {
  const retryAfter = Number(error?.response?.headers?.["retry-after"]);
  if (Number.isFinite(retryAfter) && retryAfter > 0) {
    return Math.min(retryAfter * 1000, RETRY_MAX_DELAY_MS);
  }
  const capped = Math.min(
    RETRY_BASE_DELAY_MS * 2 ** attempt,
    RETRY_MAX_DELAY_MS,
  );
  return Math.round(capped / 2 + Math.random() * (capped / 2));
}

/** Turn an axios failure into a message a person can act on. */
function toFriendlyError(error) {
  const serverMessage = error.response?.data?.error;
  if (serverMessage) {
    const wrapped = new Error(serverMessage);
    wrapped.code = error.response.data.code;
    wrapped.status = error.response.status;
    wrapped.details = error.response.data.details;
    return wrapped;
  }
  if (error.code === "ECONNABORTED") {
    return new Error(
      "The register did not respond in time, even after retrying. Try a narrower search term.",
    );
  }
  if (!error.response) {
    return new Error(
      "Could not reach the server. Check that the API is running on port 5000.",
    );
  }
  return new Error(error.message || "Something went wrong.");
}

const httpClient = axios.create({
  baseURL: "/api",
  headers: { "Content-Type": "application/json" },
  timeout: HTTP_TIMEOUT_MS,
  // Lets the browser send/receive the httpOnly refresh cookie. The app is
  // always same-origin in production (Caddy serves both), so this never
  // exposes the cookie to a third-party origin.
  withCredentials: true,
});

// Attach the in-memory access token to every request except the two auth
// calls that either don't need one (login) or must not send a stale one
// while it's mid-refresh (refresh itself, called with skipAuth below).
httpClient.interceptors.request.use((requestConfig) => {
  if (!requestConfig.skipAuth) {
    const token = getAccessToken();
    if (token) requestConfig.headers.Authorization = `Bearer ${token}`;
  }
  return requestConfig;
});

/**
 * Refreshing is coalesced into a single in-flight promise. If five requests
 * 401 at the same moment because the access token just expired, they should
 * trigger one call to /api/auth/refresh and all five wait on it — not send
 * five refresh requests and race each other's cookie rotation.
 */
let refreshPromise = null;

function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = httpClient
      .post("/auth/refresh", {}, { skipAuth: true, maxRetries: 0 })
      .then((data) => {
        setAccessToken(data.accessToken);
        return data.accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Unwrap the payload so call sites work with data, not axios envelopes.
httpClient.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const requestConfig = error.config;
    if (!requestConfig) return Promise.reject(toFriendlyError(error));

    // A request made with responseType: "blob" (file downloads) gets a
    // Blob back even when the server actually sent a JSON error body —
    // that's how axios handles it, not a bug on the server's side. Decode
    // it back to the parsed object first, so toFriendlyError below can
    // read the real message instead of falling back to a generic one.
    if (
      error.response?.data instanceof Blob &&
      error.response.data.type.includes("json")
    ) {
      try {
        error.response.data = JSON.parse(await error.response.data.text());
      } catch {
        // Leave it as-is; toFriendlyError's fallback still applies.
      }
    }

    // A 401 gets exactly one shot at silent recovery: refresh, then replay
    // the original request once. `login` and `refresh` itself opt out via
    // skipAuth, so a bad password doesn't trigger a refresh attempt, and a
    // failed refresh can't try to refresh itself into a loop.
    if (
      error.response?.status === 401 &&
      !requestConfig.skipAuth &&
      !requestConfig.__authRetried
    ) {
      requestConfig.__authRetried = true;
      try {
        const token = await refreshAccessToken();
        requestConfig.headers.Authorization = `Bearer ${token}`;
        return httpClient(requestConfig);
      } catch {
        clearAccessToken();
        notifySessionExpired();
        return Promise.reject(
          new Error("Your session has expired. Sign in again."),
        );
      }
    }

    const allowed = requestConfig.maxRetries ?? MAX_RETRIES;
    requestConfig.__retryCount = requestConfig.__retryCount || 0;

    if (requestConfig.__retryCount >= allowed || !isRetryable(error)) {
      return Promise.reject(toFriendlyError(error));
    }

    const delay = backoffDelay(requestConfig.__retryCount, error);
    requestConfig.__retryCount += 1;

    requestConfig.onRetry?.({
      attempt: requestConfig.__retryCount,
      of: allowed,
      delay,
    });

    await sleep(delay);
    return httpClient(requestConfig);
  },
);

export default httpClient;
